/**
 * The shell's second navigation level, resolved once and read by three
 * components: SectionTabs renders it, CustomAppBar names the active section in
 * the breadcrumb, and CustomLayout reserves the strip's height in the content
 * column's top inset (the bar is absolutely positioned under the app bar, so
 * the layout has to know when it is there).
 */

import { useLocation } from 'react-router-dom';
import { usePermissions } from 'react-admin';
import { useMediaQuery, useTheme } from '@mui/material';
import { TOUCH_TARGET_MIN } from '@/lightTheme';
import { resolveLocation, visibleTabs, type MenuEntry, type MenuTab, type ResolvedLocation } from './menuStructure';

/** App bar height (hybrid-3c §5), desktop and phone alike. */
export const APP_BAR_HEIGHT = 52;
/** Section bar height when it is shown. */
export const SECTION_TABS_HEIGHT = 40;
/** On a touch-sized viewport: the bar is a border-box with a 1px bottom
 *  hairline, so one extra pixel is what makes the tab itself reach the minimum. */
export const SECTION_TABS_HEIGHT_TOUCH = TOUCH_TARGET_MIN + 1;

export interface ShellSections {
  entry: MenuEntry | null;
  tab?: MenuTab;
  depth?: ResolvedLocation['depth'];
  /** The sections to render — EMPTY when the reader sees fewer than two. */
  sections: MenuTab[];
  /** Vertical space the bar occupies: 0 when it is not shown. */
  height: number;
}

export const useSectionTabs = (): ShellSections => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { permissions: role } = usePermissions<string>();

  const current = resolveLocation(location.pathname, location.search, role);
  const granted = current ? visibleTabs(current.entry, role) : [];
  const sections = granted.length > 1 ? granted : [];
  const barHeight = isMobile ? SECTION_TABS_HEIGHT_TOUCH : SECTION_TABS_HEIGHT;

  return {
    entry: current?.entry ?? null,
    tab: current?.tab,
    depth: current?.depth,
    sections,
    height: sections.length > 0 ? barHeight : 0,
  };
};

/** The active section's key, for a page that renders one section at a time. */
export const useActiveSection = (): string | undefined => useSectionTabs().tab?.key;

export default useSectionTabs;
